import { ReactNode, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Alert } from "reactstrap";
import { holdsTopBannerSlot, useTopBannerSlot } from "./topBannerSlot";

/**
 * Which of the app's alert colours the banner wears.
 *
 * `warning` is the orange the 2FA notice uses, and as of this writing the
 * release announcement uses it too - Vince's call, because the brand blue was
 * not catching anybody's eye. So the strip currently says the same thing in
 * the same colour whether somebody is being offered a feature or told they are
 * days from losing access.
 *
 * The two can never appear together, since anything action-required outranks
 * anything optional for the single slot. The cost is what orange comes to MEAN
 * after people have seen it used for news a few times: a colour that has cried
 * wolf stops carrying urgency. Worth revisiting if the 2FA notice ever starts
 * getting ignored - `primary` is the brand blue and the obvious home for news.
 */
export type SystemBannerTone =
  | "primary"
  | "info"
  | "success"
  | "warning"
  | "danger";

export interface SystemBannerProps {
  /** Identifies this banner's claim on the strip. Unique per banner. */
  id: string;
  /** From TopBannerPriority - the highest claim on screen wins the strip. */
  priority: number;
  /** Whether this banner wants to be on screen at all. */
  show: boolean;
  tone?: SystemBannerTone;
  /**
   * Called when the member closes the banner. Leave it out and no X is
   * rendered, for a notice that must not be dismissible.
   *
   * Closing is the ONLY thing that takes the banner down: following a link in
   * it leaves it up. Someone who opens a link to read more has not finished
   * with the message, and taking it away as they click removes the one thing
   * that would let them find it again.
   */
  onDismiss?: () => void;
  /** Fired when the banner actually reaches the screen, for "seen" tracking. */
  onShown?: () => void;
  "data-testid"?: string;
  children: ReactNode;
}

/**
 * The slim notice in the strip above the navbar - the app's one channel for
 * saying something to a member across every page: a release announcement, a
 * security requirement, a billing problem.
 *
 * It handles the strip itself (claiming it, yielding to anything more urgent,
 * shifting the navbar down) so callers only decide WHEN to speak, in what
 * tone, and what happens when the member closes it.
 */
export const SystemBanner = ({
  id,
  priority,
  show,
  tone = "primary",
  onDismiss,
  onShown,
  children,
  ...rest
}: SystemBannerProps) => {
  const ownsSlot = useTopBannerSlot(id, priority, show);

  // kept in a ref so a caller passing an inline arrow does not re-fire it
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;
  useEffect(() => {
    // asked of the register rather than of `ownsSlot`, which can be true on a
    // first pass that a higher-priority banner then takes back
    if (holdsTopBannerSlot(id, priority, show)) {
      onShownRef.current?.();
    }
  }, [ownsSlot, id, priority, show]);

  if (!ownsSlot) {
    return null;
  }

  return (
    <Alert
      color={tone}
      className="system-top-banner mb-0 text-center"
      data-testid={rest["data-testid"]}
      toggle={onDismiss}
    >
      {children}
    </Alert>
  );
};

export interface SystemBannerCtaProps {
  /**
   * An in-app route ("/settings/profile") or an absolute https:// link. Leave
   * it out for a CTA that does something here instead of going somewhere -
   * opening a dialog, say. It still looks like the link it sits next to.
   */
  href?: string;
  children: ReactNode;
  onClick?: () => void;
  "data-testid"?: string;
}

/**
 * The one link in a banner. External links open in a new tab so the member
 * does not lose whatever they were doing to read an announcement.
 */
export const SystemBannerCta = ({
  href,
  children,
  onClick,
  ...rest
}: SystemBannerCtaProps) => {
  const shared = {
    className: "alert-link system-top-banner-cta",
    onClick,
    "data-testid": rest["data-testid"],
  };
  if (!href) {
    // Looks like the link beside it, but acts here - opening a dialog rather
    // than sending the member away from whatever they were doing.
    return (
      <button
        type="button"
        {...shared}
        className={`${shared.className} btn btn-link p-0 align-baseline`}
      >
        {children}
      </button>
    );
  }
  return href.startsWith("/") ? (
    <Link to={href} {...shared}>
      {children}
    </Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer" {...shared}>
      {children}
    </a>
  );
};

export default SystemBanner;
